// Auto-generated industries data — DO NOT EDIT MANUALLY
// Slugs: roofing-contractors, pest-control, cleaning-services, landscaping
import type { Industry } from '../types';

export const industriesGroup4: Industry[] = [
  {
    "slug": "roofing-contractors",
    "name": "Roofing & Contractors",
    "icon": "Crane",
    "tagline": "Win More Bids. Stop Chasing Estimates.",
    "description": "BizRnR helps roofing companies respond to storm leads instantly, follow up on estimates, and book more jobs automatically.",
    "longDescription": "Roofing contractors experience extreme call volume spikes after storms — exactly when every roofer in the area is on a roof and unable to answer phones. BizRnR gives roofing companies an AI-powered phone system that captures every storm-damage call, schedules inspections, and manages the follow-up process that turns inspections into signed contracts.\n\nWhen homeowners call about storm damage or a suspected leak, BizRnR's AI gathers the address, description of damage, insurance carrier, and photos if available. The AI schedules a roof inspection at the next available slot and sends a confirmation with your company's license number and insurance information — building trust before you even arrive.\n\nPost-inspection follow-up is where roofing companies win or lose. After your inspector provides an estimate, BizRnR follows up automatically at 24 hours, 3 days, and 7 days. For insurance claim jobs, the AI helps coordinate between the homeowner and your office on claim status and approval timelines.\n\nDoor-knocking campaigns are supercharged with BizRnR: when your canvassers collect contact information in a storm-affected neighborhood, BizRnR calls every contact within hours with a professional message offering a free inspection.\n\nSeasonal maintenance programs (annual inspections, gutter cleaning, re-coating) generate recurring revenue when BizRnR proactively calls your past customer database.",
    "painPoints": [
      {
        "title": "Storm Lead Speed",
        "description": "After a storm, 50 homeowners call roofers simultaneously. First response wins."
      },
      {
        "title": "Estimate Follow-Up Inconsistency",
        "description": "Your competitor follows up three times while you wait."
      },
      {
        "title": "Seasonal Capacity Management",
        "description": "Spring and fall create more leads than you can handle manually."
      }
    ],
    "solutions": [
      {
        "title": "Storm Lead Blitz Response",
        "description": "BizRnR contacts every storm lead within 60 seconds."
      },
      {
        "title": "Estimate Follow-Up Automation",
        "description": "Multi-touch AI follow-up at 24h, 72h, and 7 days."
      },
      {
        "title": "Seasonal Campaign Scaling",
        "description": "AI scales outreach automatically during peak season."
      }
    ],
    "stats": [
      {
        "value": "60s",
        "label": "Storm lead response"
      },
      {
        "value": "44%",
        "label": "Estimate close rate"
      },
      {
        "value": "6.1x",
        "label": "Peak season ROI"
      }
    ],
    "useCases": [
      {
        "title": "Post-Hailstorm Blitz",
        "description": "Hailstorm hits Thursday. Friday BizRnR calls leads in affected zip codes."
      },
      {
        "title": "Estimate Follow-Up",
        "description": "Customer got an estimate Monday. BizRnR follows up Wednesday and Saturday."
      }
    ],
    "faqs": [
      {
        "question": "Can BizRnR target specific zip codes?",
        "answer": "Yes, upload a zip code list and BizRnR targets those areas."
      },
      {
        "question": "Does it work for insurance claim jobs?",
        "answer": "Yes, AI gathers insurance carrier info during intake."
      },
      {
        "question": "Can I manage multiple crews?",
        "answer": "Yes, configure separate territories and crews."
      },
      {
        "question": "How does it handle low-quality leads?",
        "answer": "AI qualifies every lead before booking."
      }
    ],
    "keywords": [
      "roofing lead automation",
      "contractor AI",
      "storm lead response software"
    ]
  },
  {
    "slug": "pest-control",
    "name": "Pest Control",
    "icon": "Bug",
    "tagline": "Book More Service Calls. Build Recurring Revenue.",
    "description": "BizRnR helps pest control companies book service requests, follow up on quotes, and build recurring annual contracts.",
    "longDescription": "Pest control companies thrive on speed and urgency. When a homeowner discovers termites, bed bugs, or a rodent infestation, they want help immediately — and they'll hire the first company that answers the phone. BizRnR ensures your pest control business captures every urgent call and converts it into a booked service appointment.\n\nBizRnR's AI identifies the type of pest problem, assesses urgency (active infestation vs preventive treatment), and schedules the appropriate service. For emergency situations like wasp nests near children or snake sightings, the AI dispatches your nearest available technician immediately.\n\nRegular service route optimization is a key revenue driver in pest control. BizRnR calls customers approaching their next scheduled treatment, confirms the appointment, and upsells seasonal add-on services (mosquito treatment in summer, rodent exclusion in fall). This proactive outreach fills route gaps and increases revenue per customer.\n\nNew construction and real estate inspection calls are handled professionally — BizRnR schedules WDI (Wood Destroying Insect) inspections for real estate transactions with all required details collected upfront.\n\nFor multi-location pest control operations, BizRnR routes calls based on service territory and technician availability.",
    "painPoints": [
      {
        "title": "Short Urgency Window",
        "description": "A homeowner with pests calls 3 companies. First to answer wins."
      },
      {
        "title": "One-Time to Annual Conversion",
        "description": "Most pest jobs are one-time, but recurring contracts are 5x more profitable."
      },
      {
        "title": "Seasonal Surge",
        "description": "Spring and summer inquiries spike beyond manual capacity."
      }
    ],
    "solutions": [
      {
        "title": "Instant Service Booking",
        "description": "AI answers every call and books the service appointment."
      },
      {
        "title": "Annual Plan Upsell Sequences",
        "description": "After every one-time service, AI follows up with an annual plan offer."
      },
      {
        "title": "Seasonal Campaign Automation",
        "description": "AI proactively reaches out past customers for seasonal treatments."
      }
    ],
    "stats": [
      {
        "value": "100%",
        "label": "Calls answered"
      },
      {
        "value": "41%",
        "label": "One-time to annual conversion"
      },
      {
        "value": "2.9x",
        "label": "Recurring revenue growth"
      }
    ],
    "useCases": [
      {
        "title": "Emergency Rodent Call",
        "description": "Homeowner calls Friday night. BizRnR books a Saturday morning inspection."
      },
      {
        "title": "Post-Treatment Annual Upsell",
        "description": "One week after service, BizRnR calls with a discounted annual plan offer."
      }
    ],
    "faqs": [
      {
        "question": "Can BizRnR identify pest types?",
        "answer": "AI captures descriptions and routes to the appropriate treatment type."
      },
      {
        "question": "Does it handle after-hours emergencies?",
        "answer": "Yes, available 24/7 and can dispatch your on-call technician."
      },
      {
        "question": "Can it manage service agreements?",
        "answer": "Yes, tracks intervals and reaches out when treatments are due."
      },
      {
        "question": "Does it work for commercial accounts?",
        "answer": "Yes, separate flows for residential and commercial clients."
      }
    ],
    "keywords": [
      "pest control automation",
      "exterminator AI",
      "pest control booking software"
    ]
  },
  {
    "slug": "cleaning-services",
    "name": "Cleaning Services",
    "icon": "Broom",
    "tagline": "Book More Cleans. Fill Your Calendar Automatically.",
    "description": "BizRnR helps residential and commercial cleaning companies automate quote follow-up and recurring booking reminders.",
    "longDescription": "Cleaning service companies — from residential maid services to commercial janitorial operations — compete heavily on responsiveness and professionalism. When a property manager needs emergency cleaning before a tenant move-in or a homeowner wants to book recurring service, the company that responds fastest and most professionally wins the contract. BizRnR gives cleaning companies that edge.\n\nBizRnR's AI handles new client intake by collecting property details (square footage, number of rooms, special requirements), frequency preferences (one-time, weekly, bi-weekly, monthly), and scheduling availability. The AI provides accurate time and cost estimates based on your pricing matrix, and books the first appointment.\n\nFor commercial cleaning operations, BizRnR manages multi-property scheduling and handles after-hours calls from property managers about emergency cleanups (water damage, biohazard, move-out turnovers). These high-value emergency jobs often command 2-3x standard rates and are won by whoever answers first.\n\nCustomer retention is automated through BizRnR's proactive quality check calls 24 hours after each cleaning. This simple touchpoint catches issues before they become complaints and demonstrates the level of care that earns 5-star reviews and referrals.\n\nRecurring service scheduling and holiday schedule adjustments are handled seamlessly — BizRnR calls clients before holidays to confirm, reschedule, or add extra cleanings for events.",
    "painPoints": [
      {
        "title": "Quotes Sent, No Response",
        "description": "You spend time on an estimate and the customer ghosts you."
      },
      {
        "title": "Gaps in Recurring Schedule",
        "description": "Customers skip a clean and fall off the calendar."
      },
      {
        "title": "Manual New Client Acquisition",
        "description": "Every new client requires calls and follow-up time."
      }
    ],
    "solutions": [
      {
        "title": "Quote Follow-Up Automation",
        "description": "AI follows up on every open quote at 24h, 72h, and 7 days."
      },
      {
        "title": "Recurring Booking Reminders",
        "description": "Automated reminders keep clients on schedule."
      },
      {
        "title": "New Lead Response",
        "description": "Every website or referral lead gets an instant AI response."
      }
    ],
    "stats": [
      {
        "value": "37%",
        "label": "Higher quote close rate"
      },
      {
        "value": "89%",
        "label": "Recurring client retention"
      },
      {
        "value": "3.4x",
        "label": "New client bookings"
      }
    ],
    "useCases": [
      {
        "title": "Move-In/Move-Out Rush",
        "description": "Move season: BizRnR contacts every lead and books move cleans."
      },
      {
        "title": "Skipped Clean Recovery",
        "description": "Client skips biweekly clean. BizRnR follows up to reschedule."
      }
    ],
    "faqs": [
      {
        "question": "Can BizRnR give quotes over the phone?",
        "answer": "AI collects details and provides a range quote; final pricing from your team."
      },
      {
        "question": "Does it work for commercial cleaning?",
        "answer": "Yes, separate flows for residential and commercial."
      },
      {
        "question": "Can I manage multiple cleaners?",
        "answer": "Yes, integrates with scheduling tools."
      },
      {
        "question": "Is there a minimum contract?",
        "answer": "No contracts. Monthly subscription with a 30-day money-back guarantee."
      }
    ],
    "keywords": [
      "cleaning service automation",
      "house cleaning AI",
      "commercial cleaning software"
    ]
  },
  {
    "slug": "landscaping",
    "name": "Landscaping",
    "icon": "Leaf",
    "tagline": "Fill Your Season Schedule Before Spring Hits",
    "description": "BizRnR helps landscaping companies book seasonal jobs, follow up on estimates, and reactivate past clients automatically.",
    "longDescription": "Landscaping and lawn care companies face a classic small business challenge: the owner is out in the field all day and can't answer the phone, but every missed call is a lost estimate worth $200-$5,000. BizRnR solves this by providing landscapers with an AI phone agent that sounds like your most professional office manager — available 24/7.\n\nWhen a homeowner calls about lawn care, landscape design, tree service, or hardscaping, BizRnR's AI collects the property address, describes the scope of work requested, notes any urgency (fallen tree, irrigation leak), and schedules an on-site estimate. The AI can handle questions about your services, service area, and general pricing ranges.\n\nSeasonal campaign automation drives recurring revenue: BizRnR calls your customer database before spring cleanups, fall leaf removal, snow removal season, and holiday lighting installation. These proactive calls fill your schedule weeks in advance and lock in revenue before competitors reach out.\n\nFor commercial landscape maintenance, BizRnR handles property manager communications about service schedules, weather delays, and irrigation system issues. Multi-property portfolios are managed efficiently with automated scheduling and confirmation calls.\n\nEstimate follow-up is automated: after every on-site visit, BizRnR calls the prospect at 24 hours, 3 days, and 7 days to answer questions and close the sale.",
    "painPoints": [
      {
        "title": "Short Booking Window",
        "description": "Landscaping customers book in Feb and March. Missing this window means gaps all season."
      },
      {
        "title": "Estimate No-Response",
        "description": "You estimate 20 jobs, close 8. The other 12 needed one more call."
      },
      {
        "title": "Winter Dead Zone",
        "description": "No outreach November through February means no spring bookings."
      }
    ],
    "solutions": [
      {
        "title": "Pre-Season Booking Campaigns",
        "description": "BizRnR contacts past customers in January to lock in spring bookings early."
      },
      {
        "title": "Estimate Follow-Up Automation",
        "description": "AI follows up at 48h, 5 days, and 2 weeks on every open estimate."
      },
      {
        "title": "Winter Re-Engagement",
        "description": "AI reaches out in December with seasonal services and spring booking offers."
      }
    ],
    "stats": [
      {
        "value": "2.7x",
        "label": "More spring bookings"
      },
      {
        "value": "49%",
        "label": "Estimate conversion rate"
      },
      {
        "value": "68%",
        "label": "Past client reactivation"
      }
    ],
    "useCases": [
      {
        "title": "February Pre-Season Blitz",
        "description": "January 15th: BizRnR calls every past client to book spring lawn care."
      },
      {
        "title": "Estimate Recovery",
        "description": "Sent 15 spring estimates. BizRnR follow-up converts 7 more jobs."
      }
    ],
    "faqs": [
      {
        "question": "Can BizRnR handle snow removal booking?",
        "answer": "Yes, configure a separate winter services flow."
      },
      {
        "question": "Can it target specific neighborhoods?",
        "answer": "Yes, upload your service area list."
      },
      {
        "question": "Does it work for commercial landscaping?",
        "answer": "Yes, separate flows for residential and commercial contracts."
      },
      {
        "question": "When should I start pre-season campaigns?",
        "answer": "Mid-January for spring. BizRnR automates the entire sequence."
      }
    ],
    "keywords": [
      "landscaping automation",
      "lawn care AI",
      "seasonal booking software"
    ]
  }
];
